import { WarningCircleIcon } from '@phosphor-icons/react';
import { type ReactNode, type SubmitEvent, useEffect, useRef, useState } from 'react';

import { ICON_WEIGHT } from '@/components/icon';
import { Button } from '@/components/ui/button';
import { backfillDraftsForAircraft } from '@/features/logbook';
import { loadRegister, type RegisterEntry, suggestCategory } from '@/lib/aircraft-register';
import type { Aircraft, AircraftCategory } from '@/lib/db';
import { aircraft as aircraftRepo } from '@/lib/db';
import { formatLocalDate } from '@/lib/format';
import { looksLikeRegistration, normaliseIcaoHex, normaliseRegistration } from '@/lib/parse';

import { AIRCRAFT_CATEGORIES, logbookClassFor, logbookClassLabel } from './classes';

/**
 * Add or edit one aircraft record — `PLAN.md` §6 Phase 4: "registration, type,
 * ICAO type code, class, engine count, variant notes". One form for both:
 * editing is this same panel pre-filled, the way `flights.$trackId.tsx`
 * reuses one map for every track rather than a separate "view" component.
 *
 * Typing a registration fills the rest of it in. The register the CAA
 * publishes carries every New Zealand aircraft's ICAO 24-bit address, make and
 * model, and that address is the one field here nobody can supply from memory
 * — it is assigned, not derived from the letters on the tail, so there is no
 * sum a pilot could do instead (`docs/AIRCRAFT-REGISTER.md`). What the lookup
 * fills is ordinary form state: every field stays editable, an aircraft the
 * register has never heard of is typed in exactly as before, and anything the
 * pilot has already touched is never written over. A lookup suggests; the
 * pilot decides.
 */

const INPUT_CLASS =
  'border-border bg-surface rounded-control border px-2.5 py-1.5 text-sm outline-none focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring';

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <label className="flex flex-col gap-1 text-xs">
      <span className="text-muted-foreground">{label}</span>
      {children}
    </label>
  );
}

/** What the CAA-register lookup has to say about the registration as typed. */
type LookupState =
  | { status: 'idle' }
  | { status: 'looking' }
  | { status: 'found'; entry: RegisterEntry; retrievedAt: string }
  | { status: 'missing'; retrievedAt: string }
  /** The table was never downloaded for this build — not the same as "no such aircraft". */
  | { status: 'unavailable' };

/**
 * An answer, and the registration it is an answer *about*.
 *
 * Keeping the question with the answer is what makes "still looking" a derived
 * value rather than a state to set: a result for a registration other than the
 * one in the box is simply not this box's result. Nothing has to be cleared,
 * and a reply that arrives after the pilot has typed something else cannot
 * describe the wrong aeroplane for a frame.
 */
interface LookupResult {
  registration: string;
  state: LookupState;
}

/** The register's own date, in words, or nothing if the file carried no usable one. */
function registerDate(retrievedAt: string): string {
  const formatted = formatLocalDate(retrievedAt);
  return formatted === '—' ? '' : formatted;
}

function LookupLine({ state, registration }: { state: LookupState; registration: string }) {
  if (state.status === 'idle') {
    return null;
  }
  if (state.status === 'looking') {
    return <span className="text-muted-foreground text-xs">Checking the CAA register…</span>;
  }
  if (state.status === 'unavailable') {
    return (
      <span className="text-muted-foreground text-xs leading-relaxed">
        CAA register not downloaded for this build. Fields can be filled in by hand.
      </span>
    );
  }
  if (state.status === 'missing') {
    const on = registerDate(state.retrievedAt);
    return (
      <span className="text-muted-foreground text-xs leading-relaxed">
        {registration} is not in the CAA register{on === '' ? '' : ` as it stood on ${on}`}. May be
        a new registration, or an aircraft registered elsewhere. Fields can be filled in by hand.
      </span>
    );
  }
  const described = [state.entry.make, state.entry.model].filter((part) => part !== undefined);
  return (
    <span className="text-muted-foreground text-xs leading-relaxed" data-testid="register-hit">
      From the CAA register{described.length === 0 ? '' : `: ${described.join(' ')}`}
      {state.entry.icaoHex === undefined ? '' : `, address ${state.entry.icaoHex}`}. Fields can be
      corrected if wrong.
    </span>
  );
}

export interface AircraftFormProps {
  /** Omit to create a new record; pass an existing one to edit it in place. */
  initial?: Aircraft;
  /** Pre-fills the registration on a new record — a flight's own track already knows it. Ignored when `initial` is set. */
  initialRegistration?: string;
  onSaved: (id: string) => void;
  onCancel: () => void;
}

export function AircraftForm({
  initial,
  initialRegistration,
  onSaved,
  onCancel,
}: AircraftFormProps) {
  const [registration, setRegistration] = useState(
    initial?.registration ?? initialRegistration ?? '',
  );
  const [icaoType, setIcaoType] = useState(initial?.icaoType ?? '');
  const [icaoHex, setIcaoHex] = useState(initial?.icaoHex ?? '');
  const [make, setMake] = useState(initial?.make ?? '');
  const [model, setModel] = useState(initial?.model ?? '');
  const [category, setCategory] = useState<AircraftCategory>(initial?.category ?? 'aeroplane');
  const [engineCount, setEngineCount] = useState(initial?.engineCount ?? 1);
  const [isSimulator, setIsSimulator] = useState(initial?.isSimulator ?? false);
  const [notes, setNotes] = useState(initial?.notes ?? '');
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [result, setResult] = useState<LookupResult | null>(null);

  /*
   * Who owns each field: the register, or the pilot.
   *
   * `filled` remembers the exact value the register last wrote into a field.
   * A field still holding that value is the register's to replace; a field
   * holding anything else — typed, corrected, or saved on the record already —
   * is the pilot's, and nothing here overwrites it.
   *
   * That ownership test is what makes a corrected typo safe. Typing `ZK-DFJ`
   * fills in a Cessna 152 and its address; correcting it to `ZK-DFH` has to
   * replace all of that, because the alternative is one aeroplane's
   * registration sitting above another's model and ICAO address with nothing
   * on screen saying so. The same applies when the corrected registration is
   * one the register has never heard of: what it filled is cleared, rather
   * than left behind as the wrong aircraft's details.
   *
   * `current` mirrors the fields every render so the reply to a lookup reads
   * what is on screen *now* rather than what was there when it was sent — a
   * slow first download and a fast typist are otherwise a race the typist
   * loses.
   */
  const filled = useRef(new Map<string, string>());
  const current = useRef({ icaoHex, make, model, category });
  // Mirrored in an effect rather than during render: a ref written while
  // rendering is a side effect React is entitled to run twice. Declared above
  // the lookup so that a commit has refreshed it before any reply is read.
  useEffect(() => {
    current.current = { icaoHex, make, model, category };
  }, [icaoHex, make, model, category]);

  /** The pilot typed here, so the register stops writing to it. */
  const claimField = (field: string) => filled.current.delete(field);

  /*
   * A complete-looking registration is looked up; a half-typed one is not.
   * `looksLikeRegistration` is the same conservative test the parsers use to
   * decide whether a callsign is a registration, so `ZK-D` asks nothing of the
   * register and `ZK-DFH` asks once. The register itself is downloaded at most
   * once per page load and cached in memory, so the keystroke after that is a
   * map lookup.
   */
  const normalisedRegistration = normaliseRegistration(registration);
  const worthLookingUp = looksLikeRegistration(normalisedRegistration);

  useEffect(() => {
    if (!worthLookingUp) {
      return;
    }

    let cancelled = false;
    void loadRegister().then((register) => {
      if (cancelled) {
        return;
      }

      const entry =
        register.entries.size === 0 ? undefined : register.entries.get(normalisedRegistration);
      setResult({
        registration: normalisedRegistration,
        state:
          register.entries.size === 0
            ? { status: 'unavailable' }
            : entry === undefined
              ? { status: 'missing', retrievedAt: register.retrievedAt }
              : { status: 'found', entry, retrievedAt: register.retrievedAt },
      });

      /**
       * Write one field, or take back what the register put there.
       *
       * @param saved  what the record being edited already said, which is the
       *               pilot's own answer and never overwritten.
       * @param blank  what "nothing" means for this field — `''` for text, the
       *               default for the category, which always holds something.
       */
      const write = <T extends string>(
        field: 'icaoHex' | 'make' | 'model' | 'category',
        value: T | undefined,
        saved: string | undefined,
        blank: T,
        set: (next: T) => void,
      ) => {
        if (saved !== undefined) {
          return;
        }
        const onScreen = current.current[field];
        const ours = onScreen === blank || filled.current.get(field) === onScreen;
        if (!ours) {
          return;
        }
        if (value === undefined) {
          // Only clear what the register itself put there; an empty field it
          // never filled is simply left empty.
          if (filled.current.delete(field)) {
            set(blank);
          }
          return;
        }
        filled.current.set(field, value);
        set(value);
      };

      write('icaoHex', entry?.icaoHex, initial?.icaoHex, '', setIcaoHex);
      write('make', entry?.make, initial?.make, '', setMake);
      write('model', entry?.model, initial?.model, '', setModel);
      write(
        'category',
        suggestCategory(entry?.category),
        initial?.category,
        'aeroplane',
        setCategory,
      );
    });

    return () => {
      cancelled = true;
    };
  }, [normalisedRegistration, worthLookingUp, initial]);

  /*
   * Derived rather than stored: an answer about another registration is not an
   * answer about this one, and "looking" is exactly the gap between asking and
   * being answered.
   */
  const lookup: LookupState = !worthLookingUp
    ? { status: 'idle' }
    : result?.registration === normalisedRegistration
      ? result.state
      : { status: 'looking' };

  const handleSubmit = async (event: SubmitEvent<HTMLFormElement>) => {
    event.preventDefault();
    setError(null);

    const normalised = normaliseRegistration(registration);
    if (normalised === '') {
      setError('Registration required.');
      return;
    }

    setSaving(true);
    const existing = await aircraftRepo.findByRegistration(normalised);
    if (existing !== undefined && existing.id !== initial?.id) {
      setError(`${normalised} is already on the aircraft list.`);
      setSaving(false);
      return;
    }

    const now = new Date().toISOString();
    const normalisedHex = normaliseIcaoHex(icaoHex);
    if (icaoHex.trim() !== '' && normalisedHex === '') {
      setError('An ICAO 24-bit address is up to six hexadecimal digits, e.g. c827a6.');
      setSaving(false);
      return;
    }
    const trimmedIcaoType = icaoType.trim();
    const trimmedMake = make.trim();
    const trimmedModel = model.trim();
    const trimmedNotes = notes.trim();
    const record: Aircraft = {
      id: initial?.id ?? crypto.randomUUID(),
      registration: normalised,
      ...(normalisedHex === '' ? {} : { icaoHex: normalisedHex }),
      ...(trimmedIcaoType === '' ? {} : { icaoType: trimmedIcaoType }),
      ...(trimmedMake === '' ? {} : { make: trimmedMake }),
      ...(trimmedModel === '' ? {} : { model: trimmedModel }),
      category,
      engineCount,
      isSimulator,
      ...(trimmedNotes === '' ? {} : { notes: trimmedNotes }),
      createdAt: initial?.createdAt ?? now,
      updatedAt: now,
    };
    await aircraftRepo.put(record);
    await backfillDraftsForAircraft(record);
    onSaved(record.id);
  };

  return (
    <form
      aria-label={initial === undefined ? 'Add aircraft' : `Edit ${initial.registration}`}
      className="border-border bg-surface rounded-container space-y-4 border p-4"
      onSubmit={(event) => {
        void handleSubmit(event);
      }}
    >
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        {/*
          The lookup's answer sits outside the label, not inside it like the
          other help text on this form: a label's text is the field's
          accessible name, and "Registration Checking the CAA register…" is not
          what a screen reader should call this box.
        */}
        <div className="flex flex-col gap-1">
          <Field label="Registration">
            <input
              className={INPUT_CLASS}
              value={registration}
              placeholder="ZK-ABC"
              autoFocus={initial === undefined}
              onChange={(e) => {
                setRegistration(e.target.value);
              }}
            />
          </Field>
          <LookupLine state={lookup} registration={normalisedRegistration} />
        </div>
        {/*
          In lookup order: the registration finds the address, make and model
          in the CAA register, which is what the next fields hold. Type,
          category and engines follow as what decides the logbook column.
        */}
        <Field label="ICAO 24-bit address">
          <input
            className={INPUT_CLASS}
            value={icaoHex}
            placeholder="c827a6"
            onChange={(e) => {
              claimField('icaoHex');
              setIcaoHex(e.target.value);
            }}
          />
          {/*
            Spelled out rather than labelled "hex": it is the only way to search
            an ADS-B aggregator for this aeroplane, and a pilot who
            has never needed one should be able to tell from the form what it
            is for.
          */}
          <span className="text-muted-foreground text-xs">
            From the CAA register. Used for ADS-B lookups.
          </span>
        </Field>
        <Field label="Make">
          <input
            className={INPUT_CLASS}
            value={make}
            placeholder="Cessna"
            onChange={(e) => {
              claimField('make');
              setMake(e.target.value);
            }}
          />
        </Field>
        <Field label="Model">
          <input
            className={INPUT_CLASS}
            value={model}
            placeholder="172S Skyhawk"
            onChange={(e) => {
              claimField('model');
              setModel(e.target.value);
            }}
          />
        </Field>
        <Field label="ICAO type">
          <input
            className={INPUT_CLASS}
            value={icaoType}
            placeholder="C172"
            onChange={(e) => {
              setIcaoType(e.target.value);
            }}
          />
        </Field>
        <div className="flex flex-col gap-1">
          <Field label="Category">
            <select
              className={INPUT_CLASS}
              value={category}
              onChange={(e) => {
                claimField('category');
                setCategory(e.target.value as AircraftCategory);
              }}
            >
              {AIRCRAFT_CATEGORIES.map((option) => (
                <option key={option.id} value={option.id}>
                  {option.label}
                </option>
              ))}
            </select>
          </Field>
          {/*
            The CAA register's own categories, which is why there is no
            "single-engine land" among them: the register does not record the
            difference and the engine count below does. Saying which column the
            hours land in, right here, is what keeps that from being a surprise
            at the bottom of a logbook page.
          */}
          <span className="text-muted-foreground text-xs">
            Logs under {logbookClassLabel(logbookClassFor({ category, engineCount }))}.
          </span>
        </div>
        <Field label="Engines">
          <input
            type="number"
            min={0}
            max={8}
            className={INPUT_CLASS}
            value={engineCount}
            onChange={(e) => {
              setEngineCount(Math.max(0, Math.round(Number(e.target.value) || 0)));
            }}
          />
        </Field>
        <div className="flex flex-col justify-end gap-1 pb-1.5">
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              className="accent-accent size-4"
              checked={isSimulator}
              onChange={(e) => {
                setIsSimulator(e.target.checked);
              }}
            />
            Synthetic trainer
          </label>
        </div>
      </div>

      <Field label="Notes">
        <textarea
          className={`${INPUT_CLASS} min-h-16 resize-y`}
          value={notes}
          placeholder="Variant, club aircraft, other notes"
          onChange={(e) => {
            setNotes(e.target.value);
          }}
        />
      </Field>

      {error === null ? null : (
        <p role="alert" className="text-danger flex items-center gap-1.5 text-xs">
          <WarningCircleIcon
            aria-hidden="true"
            weight={ICON_WEIGHT}
            className="size-3.5 shrink-0"
          />
          {error}
        </p>
      )}

      <div className="flex items-center gap-3">
        <Button type="submit" disabled={saving}>
          {initial === undefined ? 'Add aircraft' : 'Save changes'}
        </Button>
        <Button type="button" variant="ghost" onClick={onCancel}>
          Cancel
        </Button>
      </div>
    </form>
  );
}
